// @ts-nocheck
import { fetchMyRlTeams } from "@/lib/rlTeams";
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import {
  Users,
  Plus,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Upload
} from "lucide-react";
import InlineTeamManager from "@/components/club-coach/InlineTeamManager";
import TeamRosterSection from "@/components/club-coach/TeamRosterSection";
import RosterSubmitButton from "@/components/club-coach/RosterSubmitButton";
import { TeamEventScheduleRequests } from "@/components/club-coach/TeamEventScheduleRequests";
import { RosterIngestionDialog } from "@/components/club-coach/roster-ingestion/RosterIngestionDialog";
import { toast } from "sonner";

interface Event {
  id: string;
  name: string;
  organization_id: string;
  roster_deadline?: string;
  start_date?: string;
  end_date?: string;
  venue_name?: string;
  description?: string;
}

interface Team {
  id: string;
  name: string;
  sport?: string;
  roster_count: number;
  age_group?: string;
  logo_url?: string;
}

const VALID_TABS = ["roster", "schedule-requests"] as const;
type TabValue = (typeof VALID_TABS)[number];

const EventRosterManagementPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  const activeTab: TabValue = VALID_TABS.includes(rawTab as TabValue)
    ? (rawTab as TabValue)
    : "roster";

  const handleTabChange = (value: string) => {
    setSearchParams(value === "roster" ? {} : { tab: value }, { replace: true });
  };
  
  const [event, setEvent] = useState<Event | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [eventLoading, setEventLoading] = useState(true);
  const [sportMap, setSportMap] = useState<Record<string, string>>({});
  const [ingestionOpen, setIngestionOpen] = useState(false);

  // Fetch sport names once for display
  useEffect(() => {
    const fetchSports = async () => {
      const { data } = await supabase
        .from('sports')
        .select('id, name')
        .eq('is_active', true);
      if (data) {
        const map: Record<string, string> = {};
        data.forEach(s => { map[s.id] = s.name; });
        setSportMap(map);
      }
    };
    fetchSports();
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
    if (profile?.id) {
      fetchTeams();
    }
  }, [eventId, profile?.id, sportMap]);

  const fetchEventDetails = async () => {
    if (!eventId) return;

    try {
      setEventLoading(true);
      const { data, error } = await supabase
        .from('events')
        .select('id, name, organization_id, roster_deadline, start_date, end_date, venue_name, description')
        .eq('id', eventId)
        .single();

      if (error) {
        console.error('Error fetching event:', error);
        toast.error('Failed to load event details');
      } else {
        setEvent(data);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event details');
    } finally {
      setEventLoading(false);
    }
  };

  const fetchTeams = async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);

      const teamsData = await fetchMyRlTeams();
      {
        const formattedTeams = (teamsData || []).map((t: any) => ({
          id: String(t.id),
          name: t.display_name || t.name || 'Unnamed Team',
          sport: (t.sport_id && sportMap[t.sport_id]) || undefined,
          roster_count: Number(t.roster_count) || 0,
          age_group: t.age_group || undefined,
          logo_url: t.logo_url || undefined,
        }));
        setTeams(formattedTeams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamUpdated = () => {
    fetchTeams();
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  const handleSelectTeam = (teamId: string) => {
    setSelectedTeamId(teamId);
    setShowCreateForm(false);
  };

  const handleCreateNewTeam = () => {
    setShowCreateForm(true);
    setSelectedTeamId(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRosterDeadlineStatus = () => {
    if (!event?.roster_deadline) return null;
    
    const deadline = new Date(event.roster_deadline);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'expired', message: 'Roster deadline has passed', color: 'destructive' };
    } else if (diffDays === 0) {
      return { status: 'today', message: 'Roster deadline is today!', color: 'destructive' };
    } else if (diffDays <= 3) {
      return { status: 'urgent', message: `Roster deadline in ${diffDays} day${diffDays > 1 ? 's' : ''}`, color: 'warning' };
    } else {
      return { status: 'upcoming', message: `Roster deadline in ${diffDays} days`, color: 'default' };
    }
  };

  const deadlineStatus = getRosterDeadlineStatus();
  const selectedTeam = teams.find(team => team.id === selectedTeamId);

  if (eventLoading || loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-lg">Loading event and teams...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-lg text-red-600">Event not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary-700">Event Roster Management</h1>
          <p className="text-gray-600">{event.name}</p>
        </div>

        {/* Event Info Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Calendar className="h-5 w-5 text-primary-600" />
              <div>
                <h3 className="font-semibold">{event.name}</h3>
                {event.venue_name && (
                  <p className="text-sm text-gray-600">{event.venue_name}</p>
                )}
                {event.start_date && (
                  <p className="text-sm text-gray-600">
                    Event: {formatDate(event.start_date)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roster Deadline Alert */}
        {deadlineStatus && (
          <Alert className={deadlineStatus.color === 'destructive' ? 'border-red-200 bg-red-50' : 
                           deadlineStatus.color === 'warning' ? 'border-yellow-200 bg-yellow-50' : ''}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {deadlineStatus.message}
              {event.roster_deadline && (
                <span className="text-sm">({formatDate(event.roster_deadline)})</span>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="roster">Roster</TabsTrigger>
          <TabsTrigger value="schedule-requests">Schedule Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="mt-6">
          {/* Team Selection or Roster Management */}
          {!selectedTeamId && !showCreateForm ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Assign Roster to This Event</h2>
                <Button onClick={handleCreateNewTeam} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  + Create New Roster
                </Button>
              </div>

              {teams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map((team) => (
                    <Card
                      key={team.id}
                      className="cursor-pointer hover:border-primary-300 transition-colors"
                      onClick={() => handleSelectTeam(team.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          {team.logo_url && (
                            <img
                              src={team.logo_url}
                              alt={`${team.name} logo`}
                              className="w-10 h-10 object-cover rounded-lg border"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="font-semibold">{team.name}</h3>
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Users className="h-3 w-3" />
                              {team.roster_count} athletes
                            </div>
                            {team.sport && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {team.sport}
                              </Badge>
                            )}
                          </div>
                          <CheckCircle2 className="h-5 w-5 text-primary-600" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="p-8">
                  <div className="text-center space-y-4">
                    <Users className="h-16 w-16 text-gray-400 mx-auto" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Teams Found</h3>
                      <p className="text-gray-600 mb-6">
                        You need to create a team first to manage rosters for this event.
                      </p>
                      <Button onClick={handleCreateNewTeam} className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Create Your First Team
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          ) : showCreateForm ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Create New Roster for Event</h2>
                <Button variant="outline" onClick={handleCancelCreate}>
                  Cancel
                </Button>
              </div>
              <Card className="border-2 border-primary-200 bg-primary-50">
                <CardContent className="p-0">
                  <InlineTeamManager
                    isCreating={true}
                    onTeamUpdated={handleTeamUpdated}
                    onCancelCreate={handleCancelCreate}
                  />
                </CardContent>
              </Card>
            </div>
          ) : selectedTeam ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedTeam.logo_url && (
                    <img
                      src={selectedTeam.logo_url}
                      alt={`${selectedTeam.name} logo`}
                      className="w-10 h-10 object-cover rounded-lg border"
                    />
                  )}
                  <div>
                    <h2 className="text-xl font-semibold">Managing Roster: {selectedTeam.name}</h2>
                    <p className="text-gray-600">For event: {event.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setIngestionOpen(true)} className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Add from text, file, or photo
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedTeamId(null)}>
                    Change Roster
                  </Button>
                </div>
              </div>

              <RosterIngestionDialog
                eventId={eventId!}
                teamId={selectedTeamId}
                organizationId={event?.organization_id ?? ""}
                open={ingestionOpen}
                onOpenChange={setIngestionOpen}
                onSuccess={handleTeamUpdated}
              />

              <TeamRosterSection
                teamId={selectedTeamId}
                onRosterUpdated={handleTeamUpdated}
              />

              {/* Roster Submit Button */}
              <div className="mt-6">
                <RosterSubmitButton
                  eventId={eventId!}
                  teamId={selectedTeamId}
                  teamName={selectedTeam.name}
                  eventName={event.name}
                  playerCount={selectedTeam.roster_count}
                  rosterDeadline={event.roster_deadline}
                  onSubmitted={handleTeamUpdated}
                />
              </div>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="schedule-requests" className="mt-6">
          <TeamEventScheduleRequests
            eventId={eventId!}
            teamId={selectedTeamId ?? ""}
            organizationId={event?.organization_id ?? ""}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventRosterManagementPage;